import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

import { Menu, MenuItem, MenuSeparator } from '@/components/ui/menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import siteConfig from '@/config';
import { cn } from '@/lib/utils';
import PATH_PAGE from '@/router/path';
import { RouteParams } from '@/router/type';
import { useGetWorkspaces } from '@/service/workspace/hook';
import { useWorkspaceStore } from '@/store/workspace';
import { CaretSortIcon, CheckIcon, PlusIcon } from '@radix-ui/react-icons';
import { Building2, LayoutDashboard, User } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDemoMode } from '@/context/DemoModeContext';
import { useDemoWorkspaceStore } from '@/store/demo-workspace';

const HeaderWorkspace = () => {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, workspaceId } = useParams<RouteParams>();
  const workspaceIdByStore = useWorkspaceStore((state) => state.workspaceId);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const { isDemoMode } = useDemoMode();

  // Get demo workspaces from store
  const demoWorkspaces = useDemoWorkspaceStore((state) => state.workspaces);

  const [value, setValue] = React.useState(workspaceId || workspaceIdByStore);

  // Check if on overview page (either global or org-level overview)
  const isOverviewPage =
    location.pathname.includes('/dashboard/overview') ||
    (orgId && !workspaceId && location.pathname.endsWith(`/dashboard/${orgId}`));

  // Filter demo workspaces by current organization
  const filteredDemoWorkspaces = useMemo(() => {
    if (!isDemoMode || !orgId) return [];
    return demoWorkspaces.filter((ws) => ws.organizationId === orgId);
  }, [demoWorkspaces, orgId, isDemoMode]);

  const handleSelectWorkspace = (currentValue: string) => {
    setValue(currentValue);
    setOpen(false);

    if (!isDemoMode) {
      setWorkspace(
        data?.find((workspace) => workspace.workspace_id === currentValue) ||
          ({} as any)
      );
    }

    // Navigate to workspace dashboard
    if (isDemoMode && orgId) {
      const basePath = `/demo/dashboard/${orgId}/${currentValue}`;
      navigate(basePath);
      return;
    }

    // Non-demo mode: Replace workspace ID in current path
    if (workspaceId) {
      const currentPath = location.pathname;
      const newPath = currentPath.replace(workspaceId as string, currentValue);
      navigate(newPath);
    } else if (orgId) {
      navigate(`/dashboard/${orgId}/${currentValue}`);
    }
  };

  const { data } = useGetWorkspaces({
    staleTime: 1000 * 60 * 5,
    enabled: !isDemoMode,
  });

  // Use filtered demo workspaces in demo mode, otherwise use real workspaces
  const workspaces = isDemoMode
    ? filteredDemoWorkspaces.map((workspace) => ({
        value: workspace.id,
        label: workspace.name,
        type: workspace.type,
      }))
    : data?.map((workspace) => ({
        value: workspace.workspace_id,
        label: workspace.workspace_name,
        type: 'workspace' as const,
      })) || [];

  useEffect(() => {
    // Handle overview page - don't set a workspace value
    if (isOverviewPage) {
      setValue('');
      return;
    }

    // Skip workspace validation in demo mode
    if (isDemoMode) {
      setValue(workspaceId || '');
      return;
    }

    if (data && workspaceId) {
      const result = data.find(
        (workspace) => workspace.workspace_id === workspaceId
      );
      if (!result) {
        navigate(PATH_PAGE.setting.profile);
        return;
      }
      setValue(workspaceId);
      setWorkspace(result || ({} as any));
    } else if (data && !value) {
      const defaultWorkspace = data[0];
      setValue(defaultWorkspace.workspace_id!);
      setWorkspace(defaultWorkspace);
    }
  }, [data, workspaceId, isDemoMode, isOverviewPage]);

  // Don't render if no orgId in demo mode, or if on overview without orgId
  if (isDemoMode && !orgId) return null;
  if (!workspaceId && !orgId && !isOverviewPage) return null;

  // Show placeholder if organization is selected but no workspace yet
  const showPlaceholder = orgId && !workspaceId;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            aria-label="Select Workspace"
            className="justify-between"
          >
            {value && workspaces.find((w) => w.value === value)?.type === 'workspace' && (
              <Building2 className="mr-2 h-4 w-4 text-blue-500" />
            )}
            {value && workspaces.find((w) => w.value === value)?.type === 'personal' && (
              <User className="mr-2 h-4 w-4 text-primary" />
            )}
            {!value && showPlaceholder && (
              <LayoutDashboard className="mr-2 h-4 w-4 text-muted-foreground" />
            )}
            <span className="max-w-[150px] truncate">
              {value
                ? workspaces.find((workspace) => workspace.value === value)?.label
                : showPlaceholder
                  ? 'Select Workspace'
                  : 'Select Workspace'}
            </span>
            <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="mx-4 max-w-[300px] p-0">
          <Command>
            <CommandInput placeholder="Search workspace" className="h-9" />
            <CommandList>
              <CommandEmpty>No Workspace found.</CommandEmpty>
              {/* Workspace List */}
              <CommandGroup heading="Workspaces">
                {workspaces.length === 0 ? (
                  <CommandItem disabled>
                    No workspaces in this organization
                  </CommandItem>
                ) : (
                  workspaces.map((workspace) => (
                    <CommandItem
                      key={workspace.value}
                      value={workspace.value!}
                      onSelect={handleSelectWorkspace}
                    >
                      {workspace.type === 'workspace' && (
                        <Building2 className="mr-2 h-4 w-4 text-blue-500" />
                      )}
                      {workspace.type === 'personal' && (
                        <User className="mr-2 h-4 w-4 text-primary" />
                      )}
                      {workspace.label}
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4',
                          value === workspace.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
          {siteConfig.services.workspace?.create && !isDemoMode && (
            <Menu className="pt-0">
              <MenuSeparator />
              <MenuItem onClick={() => navigate(PATH_PAGE.dashboard.newWorkspace)}>
                <PlusIcon className="mr-2 h-4 w-4" />
                <span>Create Workspace</span>
              </MenuItem>
            </Menu>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
};

export default HeaderWorkspace;
